<template>
  <WizardDialog
    :visible="visible"
    :phase="PHASE"
    :phases="PHASES"
    :titles="titles"
    :subtitles="subtitles"
    :locked="submitting"
    width="640px"
    :close-label="t('billing.unsubscribe.keep')"
    @update:visible="onVisibleChange"
    @close="onClose"
  >
    <template #confirm>
      <div class="unsubscribe">
        <!-- Effective date card -->
        <div class="unsubscribe__date-card">
          <div class="unsubscribe__date-icon">
            <i class="fa-regular fa-calendar-xmark" />
          </div>
          <div class="unsubscribe__date-body">
            <div class="unsubscribe__date-label">
              {{ t('billing.unsubscribe.activeUntilLabel') }}
            </div>
            <div class="unsubscribe__date-value">
              {{ formattedEffectiveAt }}
            </div>
            <div class="unsubscribe__date-hint">
              {{ t('billing.unsubscribe.noChargeAfter') }}
            </div>
          </div>
        </div>

        <!-- Reason picker — card-style options -->
        <div class="unsubscribe__section">
          <div class="unsubscribe__section-title">
            {{ t('billing.unsubscribe.reasonLabel') }}
          </div>
          <div class="unsubscribe__reasons">
            <button
              v-for="opt in reasonOptions"
              :key="opt.value"
              type="button"
              class="unsubscribe__reason"
              :class="{
                'unsubscribe__reason--selected': reason === opt.value,
                'unsubscribe__reason--full': opt.value === 'other',
              }"
              :disabled="submitting"
              @click="reason = opt.value"
            >
              <span class="unsubscribe__reason-radio">
                <span
                  v-if="reason === opt.value"
                  class="unsubscribe__reason-radio-dot"
                />
              </span>
              <span class="unsubscribe__reason-icon">
                <i :class="opt.icon" />
              </span>
              <span class="unsubscribe__reason-label">{{ opt.label }}</span>
            </button>
          </div>
        </div>

        <!-- Conditional comment textarea -->
        <transition name="unsubscribe-expand">
          <div
            v-if="reason === 'other'"
            class="unsubscribe__section"
          >
            <label
              class="unsubscribe__section-title"
              for="unsubscribe-comment"
            >
              {{ t('billing.unsubscribe.commentLabel') }}
            </label>
            <Textarea
              id="unsubscribe-comment"
              v-model="comment"
              rows="3"
              class="unsubscribe__textarea"
              :placeholder="t('billing.unsubscribe.commentPlaceholder')"
              :disabled="submitting"
            />
          </div>
        </transition>

        <!-- Credits-preserved notice -->
        <div class="unsubscribe__notice">
          <i class="fa-regular fa-circle-info" />
          <span>{{ t('billing.unsubscribe.notice') }}</span>
        </div>

        <!-- Error message -->
        <Message
          v-if="errorMessage"
          severity="error"
          :closable="false"
          class="unsubscribe__error"
        >
          {{ errorMessage }}
        </Message>

        <!-- Footer actions, in-flow (kept inside the phase so spacing is
             consistent with the other billing modals — WizardDialog only
             handles the chrome, the dialog itself defines its CTA layout). -->
        <div class="unsubscribe__foot">
          <button
            type="button"
            class="unsubscribe__cta unsubscribe__cta--ghost"
            :disabled="submitting"
            @click="onClose"
          >
            <i class="fa-regular fa-arrow-left" />
            {{ t('billing.unsubscribe.keep') }}
          </button>
          <button
            type="button"
            class="unsubscribe__cta unsubscribe__cta--danger"
            :disabled="submitting"
            @click="onConfirm"
          >
            <i
              v-if="submitting"
              class="fa-regular fa-spinner-third fa-spin"
            />
            <i
              v-else
              class="fa-regular fa-check"
            />
            {{ t('billing.unsubscribe.confirm') }}
          </button>
        </div>
      </div>
    </template>
  </WizardDialog>
</template>

<script lang="ts">
import {
  computed, defineComponent, PropType, ref, watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Textarea from 'primevue/textarea';
import Message from 'primevue/message';
import moment from 'moment';
import WizardDialog from '@/components/billing/shared/WizardDialog.vue';
import { cancelPlan } from '@/composables/billing/billingApi';
import { useStructuralReload } from '@/composables/billing/useStructuralReload';

const PHASES = ['confirm'] as const;
const PHASE = 'confirm';

/**
 * Subscription cancellation modal. Single-step but rendered through
 * {@link WizardDialog} so the chrome (header / subtitle / close button)
 * matches the rest of the billing modals (BuyCredits, ChangePlan,
 * MigrateV5). Phases array has one entry — the progress bar is hidden
 * by WizardDialog when `phases.length <= 1`.
 *
 * Visual choices :
 *   - Top card highlights the « active until » date (the only piece of
 *     info the user really needs to make the decision).
 *   - Reason picker uses card-style buttons instead of stock RadioButtons
 *     for a faster click target and consistent hover feedback.
 *   - In-flow footer with ghost + danger CTAs, no PrimeVue `<template
 *     #footer>` — keeps spacing aligned with the rest of the phase.
 */
export default defineComponent({
  name: 'UnsubscribeDialog',

  components: { WizardDialog, Textarea, Message },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    effectiveAt: {
      type: String as PropType<string | null>,
      default: null,
    },
  },

  emits: ['update:visible', 'cancelled'],

  setup(props, { emit }) {
    const { t } = useI18n();
    const handleReload = useStructuralReload();

    const reason = ref<string>('too_expensive');
    const comment = ref('');
    const submitting = ref(false);
    const errorMessage = ref<string | null>(null);

    const reasonOptions = computed(() => [
      {
        value: 'too_expensive',
        label: t('billing.unsubscribe.reasons.tooExpensive'),
        icon: 'fa-regular fa-tag',
      },
      {
        value: 'closing_shop',
        label: t('billing.unsubscribe.reasons.closingShop'),
        icon: 'fa-regular fa-shop-slash',
      },
      {
        value: 'competitor',
        label: t('billing.unsubscribe.reasons.competitor'),
        icon: 'fa-regular fa-arrows-turn-to-dots',
      },
      {
        value: 'not_using_enough',
        label: t('billing.unsubscribe.reasons.notUsingEnough'),
        icon: 'fa-regular fa-chart-line-down',
      },
      {
        value: 'other',
        label: t('billing.unsubscribe.reasons.other'),
        icon: 'fa-regular fa-comment-dots',
      },
    ]);

    const formattedEffectiveAt = computed(() => (
      props.effectiveAt ? moment(props.effectiveAt).format('DD/MM/YYYY') : '—'
    ));

    const titles = computed(() => ({
      confirm: t('billing.unsubscribe.title'),
    }));
    const subtitles = computed(() => ({
      confirm: t('billing.unsubscribe.subtitle'),
    }));

    const reset = () => {
      reason.value = 'too_expensive';
      comment.value = '';
      errorMessage.value = null;
    };

    const onVisibleChange = (v: boolean) => {
      if (!v) onClose();
    };

    const onClose = () => {
      if (submitting.value) return;
      emit('update:visible', false);
      setTimeout(reset, 220);
    };

    const onConfirm = async () => {
      submitting.value = true;
      errorMessage.value = null;
      try {
        const result = await cancelPlan({
          reason: reason.value,
          comment: comment.value || undefined,
        });
        await handleReload(result, {
          onFailure: (err) => {
            errorMessage.value = err;
          },
        });
        if (result.status === 'completed') {
          emit('cancelled');
          emit('update:visible', false);
          setTimeout(reset, 220);
        }
      } finally {
        submitting.value = false;
      }
    };

    watch(() => props.visible, (v) => { if (v) reset(); });

    return {
      t,
      PHASE,
      PHASES,
      reason,
      comment,
      submitting,
      errorMessage,
      reasonOptions,
      formattedEffectiveAt,
      titles,
      subtitles,
      onVisibleChange,
      onClose,
      onConfirm,
    };
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/utilities/variables";

.unsubscribe {
  display: flex;
  flex-direction: column;
  gap: 20px;
  padding-bottom: 24px;

  // ─── Date card (top hero) ───────────────────────────────────────────
  &__date-card {
    display: flex;
    gap: 16px;
    align-items: center;
    padding: 18px 20px;
    background: linear-gradient(135deg, #fff7ed 0%, #ffffff 100%);
    border: 1px solid #fed7aa;
    border-radius: 14px;
  }

  &__date-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: #b45309;
    color: #fff;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    flex-shrink: 0;
  }

  &__date-body {
    flex: 1;
    min-width: 0;
  }

  &__date-label {
    font-size: 0.6875rem;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 700;
    color: #b45309;
  }

  &__date-value {
    font-size: 1.5rem;
    font-weight: 800;
    color: #0f172a;
    letter-spacing: -0.02em;
    line-height: 1.1;
    margin-top: 2px;
    font-variant-numeric: tabular-nums;
  }

  &__date-hint {
    font-size: 0.8125rem;
    color: #78350f;
    margin-top: 4px;
  }

  // ─── Sections (reason / comment) ────────────────────────────────────
  &__section {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  &__section-title {
    font-size: 0.8125rem;
    font-weight: 700;
    color: #0f172a;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  // ─── Reason picker — compact grid 2 cols ────────────────────────────
  // Five options : 2 cols × 2 rows for the first four, last « Autre »
  // spans the full width (it triggers the textarea, gets its own row).
  &__reasons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;

    @media (max-width: 520px) {
      grid-template-columns: 1fr;
    }
  }

  &__reason {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    padding: 9px 12px;
    background: #fff;
    border: 1.5px solid #e2e8f0;
    border-radius: 10px;
    cursor: pointer;
    text-align: left;
    transition: all 160ms ease;
    font-size: 0.875rem;
    color: #334155;
    font-family: inherit;
    min-height: 44px;

    &:hover:not(:disabled) {
      border-color: #cbd5e1;
      background: #f8fafc;
    }

    &--selected {
      border-color: $brand-color-primary;
      background: rgba(148, 200, 64, 0.06);
      color: #0f172a;
      box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.14);
    }

    // Last option (« Autre ») spans both columns so the textarea below
    // appears in a logical flow under it instead of next to it.
    &--full {
      grid-column: 1 / -1;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  &__reason-radio {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 2px solid #cbd5e1;
    flex-shrink: 0;
    position: relative;
    transition: border-color 160ms ease;

    .unsubscribe__reason--selected & {
      border-color: $brand-color-primary;
    }
  }

  &__reason-radio-dot {
    position: absolute;
    inset: 2px;
    border-radius: 50%;
    background: $brand-color-primary;
  }

  &__reason-icon {
    width: 24px;
    height: 24px;
    border-radius: 7px;
    background: #f1f5f9;
    color: #64748b;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    flex-shrink: 0;
    transition: all 160ms ease;

    .unsubscribe__reason--selected & {
      background: $brand-color-primary;
      color: #fff;
    }
  }

  &__reason-label {
    flex: 1;
    font-weight: 500;
    line-height: 1.2;
  }

  // ─── Comment textarea ───────────────────────────────────────────────
  &__textarea {
    width: 100%;
    border-radius: 10px;
    font-family: inherit;
    font-size: 0.9375rem;
    padding: 10px 12px;
    border: 1.5px solid #e2e8f0;
    resize: vertical;

    &:focus {
      border-color: $brand-color-primary;
      box-shadow: 0 0 0 3px rgba(148, 200, 64, 0.14);
      outline: none;
    }
  }

  // ─── Credits notice ─────────────────────────────────────────────────
  &__notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 10px;
    font-size: 0.8125rem;
    color: #075985;
    line-height: 1.5;

    i {
      color: #0284c7;
      flex-shrink: 0;
      margin-top: 2px;
    }
  }

  &__error {
    margin: 0;
  }

  // ─── Footer (in-flow CTAs) ──────────────────────────────────────────
  &__foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding-top: 16px;
    border-top: 1px solid #f1f5f9;
    margin-top: 4px;
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border-radius: 100px;
    font-size: 0.875rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 180ms ease;
    border: none;
    font-family: inherit;

    &:disabled {
      opacity: 0.55;
      cursor: not-allowed;
    }

    &--ghost {
      background: transparent;
      color: $brand-color-primary;
      border: 1px solid transparent;

      &:hover:not(:disabled) {
        background: rgba(148, 200, 64, 0.1);
      }
    }

    &--danger {
      background: #dc2626;
      color: #fff;
      box-shadow: 0 4px 12px -4px rgba(220, 38, 38, 0.45);

      &:hover:not(:disabled) {
        background: #b91c1c;
        transform: translateY(-1px);
        box-shadow: 0 6px 16px -4px rgba(220, 38, 38, 0.55);
      }

      &:active:not(:disabled) {
        transform: translateY(0);
      }
    }
  }
}

// ─── Comment block expand transition ──────────────────────────────────
.unsubscribe-expand-enter-active,
.unsubscribe-expand-leave-active {
  transition: all 220ms cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}
.unsubscribe-expand-enter-from,
.unsubscribe-expand-leave-to {
  opacity: 0;
  max-height: 0;
  margin-top: -20px;
}
.unsubscribe-expand-enter-to,
.unsubscribe-expand-leave-from {
  opacity: 1;
  max-height: 200px;
}
</style>
